import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import './CreateInvestmentPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

function CreateInvestmentPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [description, setDescription] = React.useState('');
  const [items, setItems] = React.useState([{ name: '', price: '' }]);
  const [submitError, setSubmitError] = React.useState(null);
  const [submitSuccess, setSubmitSuccess] = React.useState(null);

  React.useEffect(() => {
    if (!branchId) {
      navigate('/branch/store', { replace: true });
    }
  }, [branchId, navigate]);

  const addItem = () => {
    setItems((prev) => [...prev, { name: '', price: '' }]);
  };

  const removeItem = (index) => {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateItem = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const validItems = items
    .map((item) => ({
      name: String(item.name ?? '').trim(),
      price: Number(String(item.price ?? '').replace(',', '.').trim()),
    }))
    .filter((item) => item.name.length > 0 && Number.isFinite(item.price) && item.price > 0);

  const isSubmitDisabled =
    loading ||
    !String(description ?? '').trim() ||
    validItems.length === 0;

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitSuccess(null);

    const desc = String(description ?? '').trim();
    if (!desc) {
      setSubmitError('Укажите описание инвестиции');
      return;
    }

    const payload = {
      description: desc,
      items: validItems.map((item) => ({ name: item.name, price: item.price })),
    };

    const res = await requestWithMeta(
      `/operator/branches/${encodeURIComponent(branchId)}/investments`,
      'POST',
      payload
    );

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось создать инвестицию');
      return;
    }

    setSubmitSuccess('Инвестиция успешно создана');
    navigate(`/branch/${branchId}/orders`, { replace: true });
  }

  return (
    <div className="shop-create-root create-investment-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title="Создать инвестицию"
          subtitle="Описание и список позиций"
          onBack={() => navigate(`/branch/${branchId}/orders`)}
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          {submitSuccess ? (
            <section className="shop-create-section create-investment-sectionSuccess">
              <div className="shop-create-label">Готово</div>
              <div className="shop-create-help shop-create-helpTight">{submitSuccess}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <label className="shop-create-label" htmlFor="create-investment-description">
              Описание
            </label>
            <textarea
              id="create-investment-description"
              className="create-investment-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Например: вклад учредителя"
              rows={3}
              aria-describedby="create-investment-description-help"
            />
            <div id="create-investment-description-help" className="shop-create-help shop-create-helpTight">
              Кратко опишите источник инвестиции
            </div>
          </section>

          <section className="shop-create-section">
            <div className="shop-create-label">Позиции</div>

            <ul className="create-investment-list" aria-label="Список позиций инвестиции">
              {items.map((item, index) => (
                <li key={index} className="create-investment-item">
                  <input
                    type="text"
                    className="create-investment-input create-investment-inputName"
                    value={item.name}
                    onChange={(e) => updateItem(index, 'name', e.target.value)}
                    placeholder="Название"
                    aria-label={`Название позиции ${index + 1}`}
                  />
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    className="create-investment-input create-investment-inputPrice"
                    value={item.price}
                    onChange={(e) => updateItem(index, 'price', e.target.value)}
                    placeholder="Сумма"
                    aria-label={`Сумма позиции ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="create-investment-removeButton"
                    onClick={() => removeItem(index)}
                    disabled={items.length <= 1}
                    aria-label={`Удалить позицию ${index + 1}`}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <div className="create-investment-addRow">
              <div className="shop-create-help shop-create-helpTight">
                Добавьте хотя бы одну позицию с названием и суммой больше нуля
              </div>
              <button
                type="button"
                className="create-investment-addButton"
                onClick={addItem}
                aria-label="Добавить позицию"
              >
                <span className="create-investment-addButtonLabel">+ Добавить позицию</span>
              </button>
            </div>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Отправка…' : 'Создать инвестицию'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}

export default CreateInvestmentPage;
